import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Layers, ShieldCheck, Clock, ArrowRight, Droplets } from "lucide-react";
import PageHero from "@/components/PageHero";
import { getService, SERVICES, LOCALITIES, REVIEWS, GALLERY } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import FaqSection from "@/components/home/FaqSection";
import { useLead } from "@/context/LeadContext";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  const { openLead } = useLead();

  if (!service) return <Navigate to="/services" replace />;
  const d = service.detail;
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);
  const gallery = GALLERY.slice(0, 3);

  return (
    <div data-testid="service-detail-page">
      <PageHero
        title={`${service.title} in Jaipur`}
        subtitle={service.short}
        image={service.image}
        crumbs={[{ label: "Services", to: "/services" }, { label: service.title }]}
      />

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-14">
            {/* Problem */}
            <Reveal>
              <div>
                <h2 className="font-display text-3xl font-extrabold text-navy-deep mb-4">The Problem</h2>
                <p className="text-slate-600 leading-relaxed text-lg">{d.problem}</p>
              </div>
            </Reveal>

            {/* Causes + Signs */}
            <div className="grid md:grid-cols-2 gap-8">
              <Reveal>
                <div className="bg-soft-grey rounded-lg p-7 border border-slate-200 h-full">
                  <div className="flex items-center gap-2 mb-4"><AlertTriangle className="h-5 w-5 text-amber-500" /><h3 className="font-display text-xl font-bold text-navy-deep">Common Causes</h3></div>
                  <ul className="space-y-3">
                    {d.causes.map((c, i) => <li key={i} className="flex gap-2 text-slate-600 text-sm"><span className="text-royal mt-1">•</span>{c}</li>)}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="bg-soft-grey rounded-lg p-7 border border-slate-200 h-full">
                  <div className="flex items-center gap-2 mb-4"><Droplets className="h-5 w-5 text-royal" /><h3 className="font-display text-xl font-bold text-navy-deep">Signs You Need This</h3></div>
                  <ul className="space-y-3">
                    {d.signs.map((c, i) => <li key={i} className="flex gap-2 text-slate-600 text-sm"><CheckCircle2 className="h-4 w-4 text-royal mt-0.5 shrink-0" />{c}</li>)}
                  </ul>
                </div>
              </Reveal>
            </div>

            {/* Process */}
            <Reveal>
              <div>
                <h2 className="font-display text-3xl font-extrabold text-navy-deep mb-6">Our Step-by-Step Process</h2>
                <div className="space-y-3">
                  {d.process.map((p, i) => (
                    <div key={i} className="flex items-center gap-4 bg-white border border-slate-200 rounded-lg p-4 hover:border-royal/40 transition-colors">
                      <span className="h-9 w-9 rounded-md bg-royal text-white font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="text-navy-deep">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Materials */}
            <Reveal>
              <div>
                <div className="flex items-center gap-2 mb-4"><Layers className="h-5 w-5 text-royal" /><h2 className="font-display text-3xl font-extrabold text-navy-deep">Materials Used</h2></div>
                <div className="flex flex-wrap gap-3">
                  {d.materials.map((m, i) => <span key={i} className="bg-navy-deep text-white text-sm px-4 py-2 rounded-full">{m}</span>)}
                </div>
              </div>
            </Reveal>

            {/* Gallery */}
            <Reveal>
              <div>
                <h2 className="font-display text-3xl font-extrabold text-navy-deep mb-6">Project Gallery</h2>
                <div className="grid grid-cols-3 gap-3">
                  {gallery.map((g) => (
                    <div key={g.id} className="rounded-lg overflow-hidden aspect-square border border-slate-200 group">
                      <img src={g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Local SEO */}
            <Reveal>
              <div className="bg-soft-grey rounded-lg p-7 border border-slate-200">
                <h2 className="font-display text-2xl font-extrabold text-navy-deep mb-3">{service.title} Across Jaipur</h2>
                <p className="text-slate-600 leading-relaxed">
                  JAL RAKSHAKK delivers professional {service.title.toLowerCase()} throughout Jaipur — including {LOCALITIES.slice(0, 10).join(", ")} and all nearby areas. With 12+ years of experience and up to 15-year warranty, we are the trusted choice for lasting protection. Book a free inspection and get a transparent quote today.
                </p>
              </div>
            </Reveal>
          </div>

          {/* Sticky sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 space-y-6">
              <div className="bg-navy-deep rounded-lg p-7 text-white">
                <h3 className="font-display text-xl font-extrabold mb-4">Book This Service</h3>
                <div className="space-y-3 text-sm mb-5">
                  <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" /> Up to 15-Year Warranty</div>
                  <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-sky" /> Fast 1–3 Day Completion</div>
                  <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Free Inspection & Quote</div>
                </div>
                <button onClick={() => openLead("book_inspection", { prefill: { service: service.title } })} className="btn-ripple w-full bg-royal hover:bg-royal/90 text-white font-semibold py-3 rounded-sm transition-colors flex items-center justify-center gap-2" data-testid="service-book">
                  Book Free Inspection <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              <div className="bg-white rounded-lg p-6 border border-slate-200">
                <h4 className="font-display font-bold text-navy-deep mb-3">What Customers Say</h4>
                {REVIEWS.slice(0, 2).map((r) => (
                  <div key={r.name} className="mb-4 last:mb-0 border-b border-slate-100 last:border-0 pb-4 last:pb-0">
                    <p className="text-slate-600 text-sm italic">"{r.text}"</p>
                    <div className="text-xs text-navy-deep font-semibold mt-2">— {r.name}, {r.area}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg p-6 border border-slate-200">
                <h4 className="font-display font-bold text-navy-deep mb-3">Related Services</h4>
                <ul className="space-y-2">
                  {related.map((r) => (
                    <li key={r.slug}><Link to={`/services/${r.slug}`} className="text-sm text-slate-600 hover:text-royal flex items-center gap-1"><ArrowRight className="h-3 w-3" />{r.title}</Link></li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <FaqSection limit={6} />
    </div>
  );
}
